import { encode, receiptCommands } from "@tovi/pos-print";
import { api } from "@/lib/api.ts";
import { printTransport } from "@/lib/print-transport.ts";

/**
 * The print worker: claims queued jobs, renders them, sends the bytes, says
 * what happened.
 *
 * This is the piece that was missing. `pos-print` produced correct, tested
 * ESC/POS and `pos_print_jobs` accumulated documents, and nothing joined them —
 * `Engine Wiki/Printing.md` documented a pipeline that printed nothing, which is
 * the failure the wiki's two-column rule exists to prevent.
 *
 * **It runs on the till rather than the server**, because the printer is on the
 * counter and the server is not. A LAN printer is reachable from either; a USB
 * or Bluetooth one is reachable only from the device it is attached to, and
 * building for the reachable half first would mean rebuilding for the other.
 */

type ClaimedJob = {
  id: string;
  printerId: string | null;
  documentType: string;
  subjectRef: string | null;
  payload: unknown;
  renderMode: "auto" | "text" | "raster";
  lang: string;
  attempts: number;
};

/**
 * What a station ticket's payload holds, which is what `station-tickets.ts`
 * writes.
 */
type TicketPayload = {
  /** Set by the server on a receipt when the settlement took cash. */
  openDrawer?: boolean;
  /**
   * A customer receipt, already laid out by the server against the store's own
   * template. Absent on a station ticket, which is laid out below.
   */
  receiptLines?: string[] | null;
  orderNo?: string;
  tableName?: string | null;
  stationName?: string | null;
  lines?: {
    name?: string;
    quantity?: string;
    note?: string | null;
    specifications?: { name: string }[];
    toppings?: { name: string }[];
  }[];
};

/**
 * A ticket as lines of text.
 *
 * Deliberately plain: a station ticket is read at arm's length by somebody with
 * their hands full, and the only formatting that earns its place is what
 * separates one dish from the next.
 *
 * The receipt template's own layout is a server-side concern and is not
 * reimplemented here — and now genuinely is not. A customer receipt arrives
 * already laid out against the merchant's template, so this only ever composes
 * a station ticket. Until it did, a receipt fell through to the ticket layout
 * below and came off the printer with no totals on it.
 */
function ticketLines(payload: TicketPayload): string[] {
  if (payload.receiptLines && payload.receiptLines.length > 0) return payload.receiptLines;

  const lines: string[] = [];
  if (payload.stationName) lines.push(payload.stationName.toUpperCase());
  lines.push(payload.tableName ?? payload.orderNo ?? "");
  lines.push("");

  for (const line of payload.lines ?? []) {
    lines.push(`${line.quantity ?? "1"} x ${line.name ?? ""}`);
    const choices = [...(line.specifications ?? []), ...(line.toppings ?? [])]
      .map((choice) => choice.name)
      .filter(Boolean);
    if (choices.length > 0) lines.push(`   ${choices.join(", ")}`);
    // A note is the one thing on a ticket not derivable from the menu, so it is
    // the one thing that must not be quiet — on paper as on the pass.
    if (line.note) lines.push(`   ** ${line.note}`);
  }

  return lines;
}

/**
 * One pass over the queue.
 *
 * Returns how many jobs it printed, so a caller can decide whether to come back
 * sooner. Never throws: a print worker that dies on one bad job stops printing
 * everything, and the job it died on is already counted against its retries.
 */
export async function drainPrintQueue(printerIds: readonly string[]): Promise<number> {
  if (printerIds.length === 0) return 0;

  let claimed: { data: ClaimedJob[] };
  try {
    claimed = await api<{ data: ClaimedJob[] }>("/store/print-jobs/claim", {
      method: "POST",
      body: { printerIds: [...printerIds], limit: 5 },
    });
  } catch {
    // Offline, or the merchant does not have printers. Either way there is
    // nothing to say and nothing to retry here — the next tick asks again.
    return 0;
  }

  let printed = 0;
  for (const job of claimed.data) {
    try {
      const bytes = encode(
        receiptCommands({
          lines: ticketLines(job.payload as TicketPayload),
          // **`decideRenderMode` is not on this path**, and this comment used
          // to say it was. `settlement-documents.ts` stores the template's raw
          // preference, so the job carries `auto` for almost everything and the
          // decision function's only callers are a preview and a display field.
          //
          // Mapping `auto` to text is therefore a choice made here by default
          // rather than one the server made — right for a station ticket, and
          // wrong for a Khmer receipt, which comes out as `?` for every code
          // point past Latin. Deciding properly needs the rasteriser
          // `escpos.ts` still has no caller for; until then this would turn
          // silent corruption into a throw, which is not obviously better and
          // is not a call to make in a print worker.
          mode: job.renderMode === "raster" ? "raster" : "text",
          // Carried on the job rather than decided here: a drawer opens
          // because somebody has to put notes in it, which is a property of
          // the tenders, and this worker sees a queued document rather than a
          // settlement. A station ticket never sets it.
          openDrawer: (job.payload as TicketPayload).openDrawer ?? false,
        }),
      );

      await printTransport().send(job.printerId ?? "unbound", bytes);
      await settle(job.id, { outcome: "printed", error: null });
      printed += 1;
    } catch (caught) {
      await settle(job.id, {
        outcome: "failed",
        error: caught instanceof Error ? caught.message.slice(0, 240) : "The printer refused it.",
      });
    }
  }

  return printed;
}

/**
 * Says how a job ended, and swallows a failure to say so.
 *
 * If the settle call itself fails the job stays `printing` and is picked up
 * again by the attempts ceiling rather than by this client — which is why
 * `attempts` counts on the claim. Throwing here would abandon the rest of the
 * batch over a job that already printed.
 */
async function settle(
  id: string,
  body: { outcome: "printed" | "failed"; error: string | null },
): Promise<void> {
  try {
    await api(`/store/print-jobs/${id}/settle`, { method: "POST", body });
  } catch {
    /* The ceiling is the backstop. */
  }
}
